import { ApiError, type ChatReminder } from "@/api/client";
import type { RealtimeEvent } from "@/lib/realtime";
import { MOBILE_BASE } from "@/mobile/device";
import { CHAT_PARAM, layersFromParams } from "@/mobile/navLayers";
import { dayAt, fromLocalInput, inMinutes, toLocalInput } from "./scheduleTime";

// Напоминания в чате — чистая логика без React: какую карточку показать под
// плашкой звонка, что вынести в полосу над композером, как назвать срок,
// пресеты времени и проверки перед отправкой. Рисуют это ReminderCard,
// ReminderQueue, ReminderEditor и всплывашки наступивших
// (components/reminderToast).
//
// Проверки повторяют chat-service reminders.py: текст после strip — 1..500
// символов, срок в будущем и не дальше 366 дней. Сервер всё равно проверит
// сам, здесь — чтобы рекрутёр узнал об ошибке, не дожидаясь 422.

export const REMINDER_TEXT_MAX = 500;
const DUE_MAX_DAYS = 366;
const DAY_MS = 24 * 60 * 60_000;

/** Что рисовать: статус сервера плюс «пора» — активное со сроком в прошлом.
 *  Сервер считает `due` на момент ответа; время на экране идёт дальше, и
 *  карточка, пролежавшая час, должна стать «Пора» и без перезапроса. */
export type ReminderView =
  | "proposed"
  | "active"
  | "due"
  | "done"
  | "dismissed"
  | "cancelled"
  | "superseded";

export function reminderView(r: ChatReminder, now: number): ReminderView {
  if (r.status !== "active") return r.status;
  return r.due || Date.parse(r.due_at) <= now ? "due" : "active";
}

const newestFirst = (a: ChatReminder, b: ChatReminder) =>
  b.created_at.localeCompare(a.created_at);

/** Карточка под плашкой звонка: по `call_recording_id` — новейшее не
 *  superseded напоминание этого звонка (обычно оно одно: по звонку с уже
 *  принятым решением новое не предлагается). */
export function cardsByCall(list: ChatReminder[]): Map<string, ChatReminder> {
  const out = new Map<string, ChatReminder>();
  for (const r of [...list].sort(newestFirst)) {
    if (!r.call_recording_id || r.status === "superseded") continue;
    if (!out.has(r.call_recording_id)) out.set(r.call_recording_id, r);
  }
  return out;
}

/** Предложения, чьего звонка нет в загруженной ленте (он старше первой
 *  страницы истории), — в конце ленты: решения ждут, и прятать их некуда.
 *  Уже разобранные без звонка в ленте не показываем: активные и так в полосе
 *  над композером, а закрытые — история, которая догрузится вместе со звонком. */
export function unanchoredProposals(
  list: ChatReminder[],
  feedCallIds: ReadonlySet<string>,
): ChatReminder[] {
  const anchored = cardsByCall(list);
  return list
    .filter(
      (r) =>
        r.status === "proposed" &&
        !(
          r.call_recording_id &&
          feedCallIds.has(r.call_recording_id) &&
          anchored.get(r.call_recording_id) === r
        ),
    )
    .sort((a, b) => a.created_at.localeCompare(b.created_at));
}

/** Полоса над композером: активные, ближайший срок первым. */
export function queueOf(list: ChatReminder[]): ChatReminder[] {
  return list
    .filter((r) => r.status === "active")
    .sort((a, b) => a.due_at.localeCompare(b.due_at) || a.id.localeCompare(b.id));
}

/** Полоса на телефоне свёрнута в строку «Напоминания: N» (как «Отложено: N»),
 *  но наступившие видны и в свёрнутой: их пора делать, и прятать их за
 *  «Показать» — значит пропустить. `rest` — то, что прячет строка. */
export function queueParts(
  items: ChatReminder[],
  now: number,
): { due: ChatReminder[]; rest: ChatReminder[] } {
  const due: ChatReminder[] = [];
  const rest: ChatReminder[] = [];
  for (const r of items) (reminderView(r, now) === "due" ? due : rest).push(r);
  return { due, rest };
}

/** Что правится в окне полосы: то же напоминание из свежего списка (его могли
 *  перенести в другой вкладке — сравнивать правку надо с ним), а пропало оно из
 *  списка — закрыли, сменился человек — окну больше нечего править. */
export function queueEditTarget(
  items: ChatReminder[],
  editingId: string | null,
): ChatReminder | null {
  if (!editingId) return null;
  return items.find((r) => r.id === editingId) ?? null;
}

/** Порядок перечитываний напоминаний в ленте — как RefsSync у плашек ЛПР.
 *
 *  Conversation не пересоздаётся при смене человека, а действие в карточке
 *  или полосе прежнего человека может закончиться уже при новом и позвать
 *  перечитку ПРЕЖНЕГО. Она не должна ни отменить загрузку нового (тогда у него
 *  не было бы ни карточек, ни полосы до следующего события), ни лечь поверх
 *  неё. Поэтому всё — только для человека, что на экране. */
export class ReminderSync {
  private seq = 0;
  /** Человек ленты сейчас. */
  person: string | null = null;

  /** Начать перечитывание для `person`; null — это уже не тот человек. */
  begin(person: string | null): number | null {
    return person !== null && person === this.person ? ++this.seq : null;
  }

  /** Ответ перечитывания `n` про `person` ещё можно класть: позже никто не
   *  начинал, и на экране всё тот же человек. */
  accepts(n: number, person: string): boolean {
    return n === this.seq && person === this.person;
  }
}

/** Часть ключа «низа ленты» (Conversation `bottomKey`): новое предложение
 *  появляется карточкой без нового сообщения — без этого лента не спустилась
 *  бы к нему. Меняется и когда предложение разобрали: карточка сменила
 *  высоту, и низ надо удержать. Поставленное автоматически появляется так
 *  же, только сразу активным, — и в ключе оно, пока его не закрыли. */
export function proposalsKey(list: ChatReminder[]): string {
  return list
    .filter((r) => r.status === "proposed" || (r.status === "active" && r.decision === "auto"))
    .map((r) => r.id)
    .sort()
    .join(",");
}

// ---- срок словами ----

const hhmm = (d: Date) => d.toLocaleTimeString("ru-RU", { hour: "2-digit", minute: "2-digit" });
const dayDiff = (d: Date, now: Date) =>
  Math.round(
    (new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime() -
      new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime()) /
      DAY_MS,
  );

/** «сегодня в 14:00», «завтра в 11:00», «вчера в 11:00» (наступившее), иначе
 *  «пт, 25 сент., 11:00»; другой год — с годом. */
export function fmtDue(iso: string, now: Date = new Date()): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const t = hhmm(d);
  const diff = dayDiff(d, now);
  if (diff === 0) return `сегодня в ${t}`;
  if (diff === 1) return `завтра в ${t}`;
  if (diff === -1) return `вчера в ${t}`;
  const day = d.toLocaleDateString("ru-RU", {
    weekday: "short",
    day: "numeric",
    month: "short",
    ...(d.getFullYear() !== now.getFullYear() ? { year: "numeric" } : {}),
  });
  return `${day}, ${t}`;
}

/** Время в шапке карточки: сегодня — «14:05», раньше — «24 сент., 14:05». */
export function fmtStamp(iso: string, now: Date = new Date()): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  if (dayDiff(d, now) === 0) return hhmm(d);
  const day = d.toLocaleDateString("ru-RU", {
    day: "numeric",
    month: "short",
    ...(d.getFullYear() !== now.getFullYear() ? { year: "numeric" } : {}),
  });
  return `${day}, ${hhmm(d)}`;
}

// ---- пресеты и проверки ----

export interface ReminderPreset {
  label: string;
  at: Date;
}

/** «Через час», «Завтра в 11:00», «В понедельник в 11:00». Понедельник —
 *  ближайший после сегодняшнего дня; в воскресенье он совпал бы с «завтра»,
 *  и второй такой же кнопки не нужно. */
export function reminderPresets(now: Date = new Date()): ReminderPreset[] {
  const out: ReminderPreset[] = [
    { label: "Через час", at: inMinutes(60, now) },
    { label: "Завтра в 11:00", at: dayAt(1, 11, 0, now) },
  ];
  const toMonday = (8 - now.getDay()) % 7 || 7;
  if (toMonday !== 1) out.push({ label: "В понедельник в 11:00", at: dayAt(toMonday, 11, 0, now) });
  return out;
}

/** Текст напоминания: null — годится, иначе что не так. */
export function checkReminderText(text: string): string | null {
  const t = text.trim();
  if (!t) return "Напишите, о чём напомнить";
  if (t.length > REMINDER_TEXT_MAX) return `Не длиннее ${REMINDER_TEXT_MAX} символов`;
  return null;
}

/** Срок из поля datetime-local: дата или что не так. */
export function checkDue(
  value: string,
  now: Date = new Date(),
): { at: Date; error: null } | { at: null; error: string } {
  const at = fromLocalInput(value);
  if (!at) return { at: null, error: "Укажите дату и время" };
  if (at.getTime() <= now.getTime()) return { at: null, error: "Время должно быть в будущем" };
  if (at.getTime() - now.getTime() > DUE_MAX_DAYS * DAY_MS) {
    return { at: null, error: "Не дальше чем через год" };
  }
  return { at, error: null };
}

/** Срок в форме тот же, что был у напоминания (с точностью до минуты — поле
 *  секунд не хранит). Такой срок не отправляется (editPatch) и не проверяется:
 *  у наступившего он в прошлом, а поправить текст, не двигая время, можно. */
export function dueUnchanged(value: string, initialDue: Date | null): boolean {
  return initialDue !== null && value === toLocalInput(initialDue);
}

/** Проверка формы перед отправкой: текст и срок. Нетронутый срок уходит
 *  исходной датой — с её секундами, чтобы editPatch не счёл его правкой. */
export function checkReminderForm(
  text: string,
  when: string,
  initialDue: Date | null,
  now: Date = new Date(),
): { text: string; due: Date; error: null } | { error: string } {
  const textError = checkReminderText(text);
  if (textError) return { error: textError };
  if (initialDue && dueUnchanged(when, initialDue)) {
    return { text: text.trim(), due: initialDue, error: null };
  }
  const due = checkDue(when, now);
  if (due.error !== null) return { error: due.error };
  return { text: text.trim(), due: due.at, error: null };
}

/** Правка: только изменённые поля — у предложения по ним сервер отличает
 *  «принято как есть» от «принято с правкой». Срок сравнивается с точностью
 *  до минуты: поле datetime-local секунд не хранит, и нетронутый срок с
 *  секундами иначе сошёл бы за правку. Пустой объект — ничего не менялось. */
export function editPatch(
  r: Pick<ChatReminder, "text" | "due_at">,
  text: string,
  due: Date,
): { text?: string; due_at?: string } {
  const patch: { text?: string; due_at?: string } = {};
  const t = text.trim();
  if (t !== r.text.trim()) patch.text = t;
  if (toLocalInput(due) !== toLocalInput(new Date(r.due_at))) patch.due_at = due.toISOString();
  return patch;
}

/** «Через час» у наступившего: новый срок от текущего момента. */
export const snoozeDue = (now: Date = new Date()): string => inMinutes(60, now).toISOString();

// chat-service отвечает машинными кодами (HTTPException(code, "snake_code")).
const ERROR_TEXT: Record<string, string> = {
  bad_text: `Текст — от 1 до ${REMINDER_TEXT_MAX} символов`,
  due_in_past: "Время должно быть в будущем",
  due_too_far: "Не дальше чем через год",
  bad_due_at: "Сервер не понял время — выберите его ещё раз",
  not_proposed: "Предложение уже разобрали — карточка обновлена",
  not_active: "Напоминание уже закрыто — карточка обновлена",
  not_found: "Напоминание не найдено — возможно, его удалили",
  orphan: "У этого чата нет человека — напоминание завести нельзя",
};

export function reminderErrorText(err: unknown): string {
  if (err instanceof ApiError) {
    const known = ERROR_TEXT[err.message];
    if (known) return known;
    return /[А-Яа-яЁё]/.test(err.message) ? err.message : `Не получилось (код ${err.status})`;
  }
  if (err instanceof TypeError) return "Нет связи с сервером";
  return "Не получилось";
}

/** Ошибка, после которой карточку надо перечитать: её уже поменял кто-то
 *  другой (409) или её больше нет (404). */
export const reminderStale = (err: unknown): boolean =>
  err instanceof ApiError && (err.status === 409 || err.status === 404);

// ---- всплывашки наступивших (components/reminderToast) ----

/** Крестик прячет всплывашку до перезагрузки — именно этот срок: перенесённое
 *  «на час» и снова наступившее напоминание всплывёт опять. */
export const toastKey = (r: Pick<ChatReminder, "id" | "due_at">): string => `${r.id}@${r.due_at}`;

/** Что показать: наступившие, не скрытые крестиком, самые давние сверху. */
export function visibleToasts(list: ChatReminder[], hidden: ReadonlySet<string>): ChatReminder[] {
  return list
    .filter((r) => r.status === "active" && !hidden.has(toastKey(r)))
    .sort((a, b) => a.due_at.localeCompare(b.due_at) || a.id.localeCompare(b.id));
}

/** Куда ведёт «Открыть чат» всплывашки, null — никуда: чат и так открыт.
 *
 *  Полная версия — `/chats?person=`: ChatsPage следит за ним и в уже открытом
 *  инбоксе, а `?chat=` читает только при своей загрузке. Телефон — слой
 *  `?chat=` мобильной версии (mobile/navLayers): его ChatsPage применяет и на
 *  ходу, новой записью истории, которую снимет «Назад», — так же открывает
 *  чат окно входящего звонка. Тот же чат уже сверху (без карточки человека
 *  над ним) — не шагаем: лишняя запись с тем же адресом съела бы одно
 *  нажатие «Назад». */
export function reminderChatLink(
  personId: string,
  mobile: boolean,
  search: string,
): string | null {
  const id = encodeURIComponent(personId);
  if (!mobile) return `/chats?person=${id}`;
  const layers = layersFromParams(new URLSearchParams(search));
  if (layers.chat === personId && !layers.panel) return null;
  return `${MOBILE_BASE}?${CHAT_PARAM}=${id}`;
}

/** Событие потока, после которого всплывашкам надо перечитать «мои
 *  наступившие»: моё напоминание (наступило, перенесено, закрыто в другой
 *  вкладке) или одно из тех, что уже на экране (его закрыл другой рекрутёр). */
export function toastEventMatters(
  ev: RealtimeEvent,
  me: string | null,
  shown: ReadonlySet<string>,
): boolean {
  if (ev.kind !== "reminder") return false;
  if (me && ev.user_id === me) return true;
  return typeof ev.reminder_id === "string" && shown.has(ev.reminder_id);
}
